import { Capacitor } from "@capacitor/core";
import { LiveUpdate } from "@capawesome/capacitor-live-update";

// Over-the-air (OTA) update flow for the native app.
//
// On startup we:
//   1. Call `ready()` to signal the current bundle loaded successfully. This
//      confirms the bundle and prevents the plugin from rolling back to the
//      previous one. If a rollback happened we stop — the running bundle is the
//      fallback, so there's nothing newer to fetch.
//   2. Call `sync()` to check the configured cloud channel (see the LiveUpdate
//      config in capacitor.config.ts) and download a newer bundle if available.
//   3. If `sync()` reports a `nextBundleId`, call `reload()` to apply it.
//
// Everything is gated on a native platform and wrapped in try/catch so a failed
// update check can never crash the app. On web it is a no-op.
export async function runLiveUpdate(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const readyResult = await LiveUpdate.ready();
    // If the plugin rolled back to the fallback bundle, don't immediately try
    // to re-apply an update this launch.
    if (readyResult.rollback) {
      return;
    }

    const syncResult = await LiveUpdate.sync();
    if (syncResult.nextBundleId !== null) {
      await LiveUpdate.reload();
    }
  } catch (error) {
    // Never let an OTA check break app startup.
    console.error("Live update check failed", error);
  }
}
